import { isContinuousTelemetryPair } from '@kangaroo/shared';

import { speedColorBrakeToFlatOut, type TrackMapPoint } from './telemetrySession';

export type PrecisionTrackBounds = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

export type PrecisionTrackViewport = {
  centerX: number;
  centerY: number;
  width: number;
  height: number;
};

export type PrecisionTrackSegment = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
};

/** The web line-map's projected geometry and speed coloring, shared with native SVG. */
export function buildPrecisionTrackSegments(
  points: TrackMapPoint[],
  speedRange?: { min: number; max: number },
): PrecisionTrackSegment[] {
  if (points.length < 2) return [];

  const speeds = points.map((point) => point.speedKph);
  const minSpeed = speedRange?.min ?? Math.min(...speeds);
  const maxSpeed = speedRange?.max ?? Math.max(...speeds);

  return points.slice(1).flatMap((point, index) => {
    const previous = points[index]!;
    if (!isContinuousTelemetryPair(previous, point)) return [];
    const speed = (previous.speedKph + point.speedKph) / 2;
    return [
      {
        x1: previous.x,
        y1: previous.y,
        x2: point.x,
        y2: point.y,
        color: speedColorBrakeToFlatOut(speed, minSpeed, maxSpeed),
      },
    ];
  });
}

export function precisionTrackBounds(points: TrackMapPoint[]): PrecisionTrackBounds {
  const finitePoints = points.filter(
    (point) => Number.isFinite(point.x) && Number.isFinite(point.y),
  );
  if (!finitePoints.length) return { minX: 0, maxX: 640, minY: 0, maxY: 280 };

  return finitePoints.reduce(
    (bounds, point) => ({
      minX: Math.min(bounds.minX, point.x),
      maxX: Math.max(bounds.maxX, point.x),
      minY: Math.min(bounds.minY, point.y),
      maxY: Math.max(bounds.maxY, point.y),
    }),
    {
      minX: finitePoints[0]!.x,
      maxX: finitePoints[0]!.x,
      minY: finitePoints[0]!.y,
      maxY: finitePoints[0]!.y,
    },
  );
}

/** Preserve track proportions and the web review's space below the route for replay. */
export function fitPrecisionTrackViewport(
  bounds: PrecisionTrackBounds,
  containerAspect: number,
  reserveReplayDock = true,
): PrecisionTrackViewport {
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const trackHeight = bounds.maxY - bounds.minY;
  const replayDockInset = reserveReplayDock ? trackHeight * 0.12 : 0;
  const centerY = (bounds.minY + bounds.maxY + replayDockInset) / 2;
  const paddedWidth = Math.max(32, (bounds.maxX - bounds.minX) * 1.14);
  const paddedHeight = Math.max(32, trackHeight * 1.14 + replayDockInset);
  if (paddedWidth / paddedHeight > containerAspect) {
    return { centerX, centerY, width: paddedWidth, height: paddedWidth / containerAspect };
  }
  return { centerX, centerY, width: paddedHeight * containerAspect, height: paddedHeight };
}
